import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { fs } from '../firebase';
import { LocalizationContext } from '../localization/LocalizationContext';
import { RESTAURANT_META } from '../config/restaurantMeta';
import heroVideo from '../img/13.mov';
import '../css/LandingPage.css';

const EXCLUDED_HOME_BRANDS = new Set(['terraalta', 'terra-alta', 'terra alta', 'cave', 'fastdelivery']);

const LandingPage = () => {
    const { t } = useTranslation();
    useContext(LocalizationContext);
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const brandsSliderRef = useRef(null);
    const [cmsConfig, setCmsConfig] = useState(null);
    const [muted, setMuted] = useState(true);

    useEffect(() => {
        getDoc(doc(fs, 'cms', 'landingConfig'))
            .then(snap => { if (snap.exists()) setCmsConfig(snap.data()); })
            .catch(() => {});
    }, []);

    const brands = Object.values(RESTAURANT_META).filter((restaurant) => {
        const normalizedUrl = String(restaurant.url || '').toLowerCase();
        const normalizedName = String(restaurant.name || '').toLowerCase();
        if (!restaurant.url || EXCLUDED_HOME_BRANDS.has(normalizedUrl) || EXCLUDED_HOME_BRANDS.has(normalizedName)) return false;
        if (cmsConfig?.brands?.[restaurant.url]?.visible === false) return false;
        return true;
    });

    const scrollBrands = (dir) => {
        const el = brandsSliderRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * 280, behavior: 'smooth' });
    };

    const goToRestaurant = (url) => {
        localStorage.setItem('selectedCompany', url);
        navigate(`/${url}`);
    };

    return (
        <div className="lp">
            <section className="lp-hero">
                <div className="lp-video-wrap">
                    <video
                        ref={videoRef}
                        className="lp-video-frame"
                        src={heroVideo}
                        autoPlay
                        muted={muted}
                        loop
                        playsInline
                    />
                    <div className="lp-hero-gradient" />
                </div>

                <button className="lp-hero-sound-btn" onClick={() => setMuted(p => !p)} aria-label={muted ? 'Unmute video' : 'Mute video'}>
                    {muted ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                            <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                            <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                            <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                        </svg>
                    )}
                </button>
            </section>

            <section className="lp-brands-section">
                <div className="lp-brands-header lp-visible">
                    <div>
                        <h2 className="lp-section-title">{t('Our Brands')}</h2>
                    </div>
                    <div className="lp-brands-slider-controls">
                        <button className="lp-brands-nav-btn" onClick={() => scrollBrands(-1)} aria-label="Previous">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 18l-6-6 6-6"/>
                            </svg>
                        </button>
                        <button className="lp-brands-nav-btn" onClick={() => scrollBrands(1)} aria-label="Next">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 18l6-6-6-6"/>
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="lp-brands-slider-outer">
                    <div className="lp-brands-slider" ref={brandsSliderRef}>
                        {brands.map((restaurant) => {
                            const cms = cmsConfig?.brands?.[restaurant.url] || {};
                            const bg = cms.bgUrl || restaurant.bg;
                            const logo = cms.logoUrl || restaurant.logo;
                            const name = cms.name || restaurant.name;
                            const type = cms.type || restaurant.type;
                            return (
                                <div
                                    key={restaurant.url}
                                    className={`lp-brand-slide${restaurant.comingSoon ? ' lp-brand-slide--soon' : ''}`}
                                    onClick={() => !restaurant.comingSoon && goToRestaurant(restaurant.url)}
                                >
                                    <div className="lp-brand-slide-card">
                                        <img src={bg} alt={name} className="lp-brand-slide-img" />
                                        <div className="lp-brand-slide-overlay" />
                                        {logo && (
                                            <div className="lp-brand-slide-logo-wrap">
                                                <img src={logo} alt={name} className={`lp-brand-slide-logo${restaurant.coloredLogo ? ' lp-brand-slide-logo--colored' : ''}`} />
                                            </div>
                                        )}
                                        {restaurant.comingSoon && <span className="lp-brand-soon-badge">{t('Coming Soon')}</span>}
                                    </div>
                                    <div className="lp-brand-slide-name">{name}</div>
                                    <div className="lp-brand-slide-type">{t(type)}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default LandingPage;
